import {
  ColumnDef,
  PaginationState,
  getCoreRowModel,
  useReactTable,
  OnChangeFn,
  SortingState,
  ColumnSort,
} from "@tanstack/react-table";
import { z, ZodObject, ZodRawShape } from "zod";
import { useAppDispatch, useAppSelector } from "./useTypedSelector";
import { useEffect, useMemo, useState } from "react";
import {
  clear,
  crudReadAll,
  initTable,
} from "../root/general/infrastructure/slices/crudTableSlice/crudTableSlice";
import { CrudTable } from "../root/general/modules/commons/crud/crudTable";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Search, TrashFill } from "react-bootstrap-icons";
import {
  CustomFilters,
  GraphqlFilterRoot,
} from "../root/general/crudGraphql/domain/models/graphqlModel";
import {
  addSelectedId,
  removeSelectedId,
  resetBulkValues,
} from "../root/general/infrastructure/slices/bulkSelectSlice/bulkSelectSlice";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { getSticker } from "../root/qrcode/infrastructure/slices/qrMutateSlice";
import { BulkActions } from "../root/general/modules/commons/bulk/BulkActions";
import { CrudTableSubTableProps } from "../root/general/modules/commons/crud/crudTableModel";
import { AllRequestQueryResponseModel } from "../root/request/domain/models/requestModel";
import { getAllRequestStates } from "../root/request/infrastructure/slices/requestMutateSlice";
import { useAuthorization } from "./useAuthorization";
import { UserRole } from "../root/auth/domain/types/userRoleType";
import { extractDealerInfo } from "../root/general/utils/extractDealerInfo";

interface CrudTableModel {
  prefix: string;
  entity: string;
  columns: ColumnDef<z.infer<ZodObject<ZodRawShape>>>[];
  schema: ZodObject<ZodRawShape>;
  addAction?: string | (() => void) | undefined;
  editAction?: string | ((id: string) => void) | undefined;
  deleteAction?: string | ((id: string) => void) | undefined;
  columnAction?: string | ((id: string) => void) | undefined;
  bulkSticker?: {
    type: string;
    idField: string;
  };
  bulkFields?: {
    field: string;
    options?: { label: string; value: string }[];
    date?: boolean;
  }[];
  filters?: GraphqlFilterRoot | CustomFilters;
  queryExclude?: string[];
  defaultSorting?: ColumnSort[];
  enableCustomSorting?: boolean;
  customSortData?: ((data: unknown[]) => unknown[]) | null;
  overrideTextPrefix?: string;
  subTable?: (props: CrudTableSubTableProps) => JSX.Element;
  bulkRoles?: UserRole[];
  bulkFieldsResponseExclude?: string[];
  bulkFieldsRequiredGroup?: string[];
  bulkFieldsMessageAboveRequiredGroup?: string;
  dealerPath?: string; // Path to dealer info in the entity, e.g. "car.relation"
}

export default function useCrudTable({
  prefix,
  entity,
  columns,
  schema,
  addAction,
  editAction,
  deleteAction,
  columnAction,
  bulkSticker,
  bulkFields,
  filters,
  queryExclude,
  defaultSorting = [],
  enableCustomSorting = false,
  customSortData,
  overrideTextPrefix,
  subTable,
  bulkRoles,
  bulkFieldsResponseExclude,
  bulkFieldsRequiredGroup,
  bulkFieldsMessageAboveRequiredGroup,
  dealerPath,
}: CrudTableModel) {
  const { t } = useTranslation("common");

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { hasRole } = useAuthorization();

  const pageName = location.pathname.split("/").pop();

  const { data, total } = useAppSelector((state) => state.crudTable);

  const { updated } = useAppSelector((state) => state.crudMutate);

  const [allRequestStates, setAllRequestStates] = useState<
    AllRequestQueryResponseModel | undefined
  >();

  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  const selectedIds = useSelector(
    (state: RootState) =>
      state.bulkSelected.pageSelection.find((page) => page.page === pageName)
        ?.ids,
  );

  const [searchParams] = useSearchParams();

  const [showBulkPopup, setShowBulkPopup] = useState(false);
  const toggleBulkPopup = () => {
    document.body.style.overflow = showBulkPopup ? "auto" : "hidden";
    setShowBulkPopup(!showBulkPopup);
  };

  let showBulkSticker = undefined;
  if (bulkSticker) {
    showBulkSticker = () => {
      if (!bulkSticker?.type || !selectedIds) {
        return;
      }
      const result = dispatch(
        getSticker({
          type: bulkSticker.type,
          ids: selectedIds,
        }),
      ).unwrap();

      void result.then((result) => {
        if (result.pdfUrl) {
          window.open(result.pdfUrl, "_blank");
        }
      });
    };
  }

  useEffect(() => {
    const page = searchParams.get("page");
    if (!page) {
      navigate("?page=1", { replace: true });
    } else {
      setPagination({
        pageIndex: parseInt(page) - 1,
        pageSize: enableCustomSorting ? 1000 : 50,
      });
    }

    const sort = searchParams.get("sort");
    if (typeof sort == "string" && sort !== "") {
      const [id, sortDirection] = sort.split(":");

      const obj = [
        {
          desc: sortDirection === "Desc",
          id: id,
        },
      ] as SortingState;

      setSorting(obj);
    } else if (sort == "") {
      setSorting([]);
    } else {
      setSorting(defaultSorting);
    }
  }, [searchParams, enableCustomSorting]);

  const updatePagination: OnChangeFn<PaginationState> = (updaterOrValue) => {
    const pagegState =
      typeof updaterOrValue === "function"
        ? updaterOrValue(pagination)
        : updaterOrValue;

    const pageNumber = pagegState.pageIndex + 1;

    const searchParams = new URLSearchParams(location.search);
    searchParams.set("page", String(pageNumber));

    navigate(`?${searchParams.toString()}`);
  };

  const updateSorting: OnChangeFn<SortingState> = (updaterOrValue) => {
    const sortingState =
      typeof updaterOrValue === "function"
        ? updaterOrValue(sorting)
        : updaterOrValue;

    const sortParams = sortingState
      .map((sort) => `${sort.id}:${sort.desc ? "Desc" : "Asc"}`)
      .join(",");

    const searchParams = new URLSearchParams(location.search);
    searchParams.set("sort", sortParams);

    navigate(`?${searchParams.toString()}`);
  };

  useEffect(() => {
    if (!allRequestStates) {
      const result = dispatch(getAllRequestStates()).unwrap();
      void result.then((result) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setAllRequestStates(result);
      });
    }
    dispatch(resetBulkValues());
    dispatch(initTable({ schema, prefix, queryExclude }));
    return () => {
      dispatch(clear());
    };
  }, []);

  useEffect(() => {
    void dispatch(
      crudReadAll({
        entity,
        ...pagination,
        filters: filters,
        sorting: sorting,
      }),
    );
  }, [pagination, filters, sorting]);

  useEffect(() => {
    if (updated) {
      void dispatch(
        crudReadAll({
          entity,
          ...pagination,
          filters: filters,
          sorting: sorting,
        }),
      );
    }
  }, [updated]);

  const triggerUpdate = () => {
    void dispatch(
      crudReadAll({
        entity,
        ...pagination,
        filters: filters,
        sorting: sorting,
      }),
    );
  };

  columns.map(function (data) {
    if (data.meta == undefined) {
      data.meta = {};
    }

    if (columnAction != undefined && data.meta.columnAction == undefined) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      data.meta.columnAction = columnAction;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    data.meta.triggerUpdate = triggerUpdate;
  });

  const canShowBulk = bulkRoles
    ? bulkRoles.some((role) => hasRole(role))
    : true;

  if (
    canShowBulk &&
    ((bulkFields != undefined && bulkFields.length > 0) ||
      bulkSticker != undefined)
  ) {
    columns = [
      {
        id: "select",
        header: "Select",
        accessorKey: "id",
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        size: 5,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        cell: ({ row }) => {
          const rowId = row.original.id;
          const isSelected = selectedIds?.includes(rowId);

          // Extract dealer info using configurable path
          let dealerInfo: { id: string; name: string } | null = null;
          if (dealerPath) {
            dealerInfo = extractDealerInfo(
              row.original as Record<string, unknown>,
              dealerPath,
            );
          }

          return (
            <div className="selectable-div">
              <input
                className="selectable-input"
                type="checkbox"
                checked={isSelected}
                onChange={(event) => {
                  if (event.target.checked) {
                    dispatch(
                      addSelectedId({
                        id: rowId,
                        page: pageName as string,
                        dealerInfo: dealerInfo,
                      }),
                    );
                  } else {
                    dispatch(
                      removeSelectedId({
                        id: rowId,
                        page: pageName as string,
                      }),
                    );
                  }
                }}
              />
            </div>
          );
        },
      },
      ...columns,
    ];
  }

  if (
    (editAction !== undefined && editAction !== "") ||
    (deleteAction !== undefined && deleteAction !== "")
  ) {
    columns = [
      ...columns,
      {
        header: "Actions",
        cell: ({ cell }) => {
          let id = cell.row.original.id;

          let editButton = null;
          if (editAction && editAction !== "") {
            if (cell.row.original.car !== undefined) {
              id = cell.row.original.car.id;
            }
            if (typeof editAction === "function") {
              editButton = (
                <Button onClick={() => editAction(id)} size="sm">
                  <Search size={16} color="white" />
                </Button>
              );
            } else {
              editButton = (
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                <Link to={editAction.replace(":id", id.toString())}>
                  <Button
                    className="entity-select-add-button"
                    variant={"primary"}
                    size="sm"
                  >
                    <Search size={12} color="white" />
                  </Button>
                </Link>
              );
            }
          }

          let deleteButton = null;
          if (deleteAction && deleteAction !== "") {
            if (typeof deleteAction === "function") {
              deleteButton = (
                <Button
                  onClick={() => deleteAction(cell.row.original.id)}
                  size="sm"
                  style={editButton ? { marginLeft: "8px" } : {}}
                  variant={"light"}
                >
                  <TrashFill size={16} color="black" />
                </Button>
              );
            } else {
              deleteButton = (
                <Link
                  to={deleteAction.replace(
                    ":id",
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                    cell.row.original.id.toString(),
                  )}
                >
                  <Button
                    className="entity-select-add-button"
                    style={editButton ? { marginLeft: "8px" } : {}}
                    variant={"light"}
                    size="sm"
                  >
                    <TrashFill size={12} color="black" />
                  </Button>
                </Link>
              );
            }
          }

          return (
            <>
              {editButton && <>{editButton}</>}
              {deleteButton && <>{deleteButton}</>}
            </>
          );
        },
      },
    ];
  }

  // Call the custom sorting when enabled.
  const sortedData = useMemo(() => {
    if (enableCustomSorting && customSortData && data) {
      return customSortData(data);
    }
    return data || [];
  }, [data, customSortData, enableCustomSorting]);

  // Modify columns to disable sorting when custom sorting is active
  const modifiedColumns = useMemo(() => {
    if (enableCustomSorting) {
      return columns.map((col) => ({
        ...col,
        enableSorting: false,
      }));
    }
    return columns;
  }, [columns, enableCustomSorting]);

  const table = useReactTable({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columns: modifiedColumns as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: (sortedData ?? []) as any,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: total,
    state: {
      pagination: pagination,
      sorting: sorting,
    },
    manualSorting: true,
    onSortingChange: updateSorting,
    onPaginationChange: updatePagination,
  });

  const createPrefix = (prefix: string): string => {
    if (screen.width < 500) {
      return `crud.create.btn.mobile`;
    }
    return `crud.${prefix}.create.btn`;
  };

  // Get dealer tracking from store for dealer analysis
  const selectedItemsByDealer = useSelector(
    (state: RootState) => state.bulkSelected.selectedItemsByDealer,
  );
  const selectedItemsWithoutDealer = useSelector(
    (state: RootState) => state.bulkSelected.selectedItemsWithoutDealer,
  );

  // Analyze selected tire sets for dealer information using tracked data
  const dealerAnalysis = useMemo(() => {
    if (!selectedIds || selectedIds.length === 0) {
      return {
        hasCommonDealer: false,
        commonDealer: null,
        multipleDealers: false,
      };
    }

    const dealerIds = Object.keys(selectedItemsByDealer);
    const totalSelectedItems = selectedIds.length;
    const itemsWithDealers = Object.values(selectedItemsByDealer)
      .map((dealerData) => dealerData.tireSetIds.length)
      .reduce((sum, count) => sum + count, 0);

    // Check if all selected items have dealers and they're all the same dealer
    if (dealerIds.length === 1 && itemsWithDealers === totalSelectedItems) {
      // All selected items have the same dealer
      const dealerId = dealerIds[0];
      const dealerData = selectedItemsByDealer[dealerId];

      return {
        hasCommonDealer: true,
        commonDealer: { id: dealerId, name: dealerData.dealerName ?? "" },
        multipleDealers: false,
      };
    } else if (dealerIds.length > 1 || selectedItemsWithoutDealer.length > 0) {
      // Multiple different dealers or some items without dealers
      return {
        hasCommonDealer: false,
        commonDealer: null,
        multipleDealers: dealerIds.length > 1,
      };
    } else {
      // No dealers at all
      return {
        hasCommonDealer: false,
        commonDealer: null,
        multipleDealers: false,
      };
    }
  }, [selectedIds, selectedItemsByDealer, selectedItemsWithoutDealer]);

  const bulk =
    canShowBulk && bulkFields && bulkFields.length > 0 ? (
      <BulkActions
        entity={entity}
        bulkFields={bulkFields}
        selectedIds={selectedIds}
        pageName={pageName as string}
        showBulkPopup={showBulkPopup}
        toggleBulkPopup={toggleBulkPopup}
        bulkFieldsResponseExclude={bulkFieldsResponseExclude}
        bulkFieldsRequiredGroup={bulkFieldsRequiredGroup}
        bulkFieldsMessageAboveRequiredGroup={
          bulkFieldsMessageAboveRequiredGroup
        }
        dealerAnalysis={dealerPath ? dealerAnalysis : undefined}
      />
    ) : null;
  return {
    tableInstance: table,
    table: (
      <CrudTable
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        table={table as any}
        prefix={prefix}
        showBulkPopup={showBulkPopup}
        setShowBulkPopup={setShowBulkPopup}
        showBulkSticker={showBulkSticker}
        subTable={subTable}
        dealerPath={dealerPath}
      />
    ),
    addButton: addAction ? (
      addAction instanceof Function ? (
        <Button size="sm" className={"responsive-button"} onClick={addAction}>
          {t(createPrefix(overrideTextPrefix ?? prefix))}
        </Button>
      ) : (
        <Link to={addAction}>
          <Button size="sm" className={"responsive-button"}>
            {t(createPrefix(overrideTextPrefix ?? prefix))}
          </Button>
        </Link>
      )
    ) : null,
    bulk,
  };
}
